import { Component, Inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

import { FinancingHold } from 'src/app/api/models';
import { FinancingHoldsService } from 'src/app/api/services';
import { describeFinancingHold, refusalMessage } from 'src/app/shared/financing-holds/financing-hold-text';

export interface ReleaseFinancingHoldDialogData {
  hold: FinancingHold;
}

/**
 * ReleaseFinancingHoldDialogComponent releases one financing hold from the
 * invoice page with a required note (add-financing-holds, design D13).
 *
 * The dialog posts the release itself, so a refusal is shown inside it and
 * the dialog stays open. It closes with the released hold, and with
 * `undefined` on cancel, escape or a backdrop click.
 *
 * The disabled submit is a convenience only: the server trims the note and
 * refuses a blank one with `422 financing_hold_release_note_required`.
 */
@Component({
  selector: 'app-release-financing-hold-dialog',
  templateUrl: './release-financing-hold-dialog.component.html',
  styleUrls: ['./release-financing-hold-dialog.component.scss'],
})
export class ReleaseFinancingHoldDialogComponent {
  public readonly form = new UntypedFormGroup({ note: new UntypedFormControl('') });
  public readonly description: string;
  public saving = false;
  public errorMessage: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<ReleaseFinancingHoldDialogComponent, FinancingHold>,
    @Inject(MAT_DIALOG_DATA) public data: ReleaseFinancingHoldDialogData,
    private financingHoldsService: FinancingHoldsService,
  ) {
    this.description = describeFinancingHold(data.hold);
  }

  // The trimmed note both gates the submit and is what is sent.
  public get note(): string {
    return ((this.form.value.note as string | null) ?? '').trim();
  }

  public get canSubmit(): boolean {
    return !this.saving && this.note !== '';
  }

  public submit(): void {
    if (!this.canSubmit) {
      return;
    }
    this.saving = true;
    this.errorMessage = null;
    this.financingHoldsService
      .releaseFinancingHold({ id: this.data.hold.id, body: { note: this.note } })
      // A 403 completes without a value: the HTTP error interceptor shows
      // its own dialog. Re-enable the submit either way.
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (hold: FinancingHold) => this.dialogRef.close(hold),
        error: (err: unknown) => {
          this.errorMessage = refusalMessage(err, 'Could not release the hold. Please try again.');
        },
      });
  }

  public cancel(): void {
    this.dialogRef.close(undefined);
  }
}
